/* /upgrade — WildQuest Explorer subscription plan picker.
 *
 * iter31 ships the FULL UX up to (and not including) Stripe checkout.
 * The primary CTA on each plan card is disabled with a "Coming soon"
 * badge until iter32 wires POST /api/billing/checkout-session.
 *
 * When iter32 lands, only the two button handlers change — everything
 * else (copy, price fetch, plan cards, comparison, admin config link) stays.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Check, ArrowLeft, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useT } from '../i18n/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const FEATURE_KEYS = [
  'unlimitedProgression',
  'unlimitedArena',
  'unlimitedCrafting',
  'unlimitedCollections',
  'unlimitedHistory',
  'unlimitedChallenges',
  'monthlyRewards',
  'exclusiveCosmetics',
  'supportsDevelopment',
];

export default function Upgrade() {
  const nav = useNavigate();
  const t = useT();
  const [status, setStatus] = useState(null);
  const [checkout, setCheckout] = useState(null);   // 'monthly' | 'yearly' | null
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get('/subscription/status')
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/billing/portal', { origin_url: window.location.origin });
      if (!data?.portal_url) throw new Error('No portal URL');
      window.location.assign(data.portal_url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('upgrade.portalUnavailable', 'Portal unavailable.');
      toast.error(t('upgrade.couldNotOpenPortal', 'Could not open portal'), { description: String(msg) });
      setPortalLoading(false);
    }
  };

  const startCheckout = async (plan) => {
    setCheckout(plan);
    try {
      const lookup_key = plan === 'monthly' ? 'wildquest_pro_monthly' : 'wildquest_pro_yearly';
      // Remember where the player came from so /upgrade/success can return them.
      const return_path = sessionStorage.getItem('wq_upgrade_return') || '/dashboard';
      const { data } = await api.post('/billing/checkout', {
        lookup_key,
        origin_url: window.location.origin,
        return_path,
      });
      if (!data?.checkout_url) throw new Error('No checkout URL returned');
      window.location.assign(data.checkout_url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('upgrade.checkoutFailed', 'Checkout failed.');
      toast.error(t('upgrade.couldNotStartCheckout', 'Could not start checkout'), { description: String(msg) });
      setCheckout(null);
    }
  };

  const monthly = status?.prices?.monthly_usd ?? 4.99;
  const yearly  = status?.prices?.yearly_usd ?? 44.99;
  const savings = Math.max(0, Math.round((1 - yearly / (monthly * 12)) * 100));
  const isPro = status?.tier === 'pro' || status?.tier === 'admin';

  return (
    <div className="space-y-5 pb-24" data-testid="upgrade-root">
      <div className="flex items-center gap-2">
        <button onClick={() => nav(-1)} data-testid="upgrade-back"
          className="p-1.5 rounded-full glass border border-white/10">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest text-accent">{t('upgrade.membership', 'Membership')}</p>
          <h1 className="font-serif text-3xl leading-tight">{t('upgrade.title', 'Continue your Adventure')}</h1>
        </div>
        <LanguageSwitcher compact />
      </div>

      <p className="text-sm text-white/80 leading-relaxed">
        {t('upgrade.intro', "You've explored the world. Built Battle Cards. Completed Expeditions. Now unlock unlimited progression and support the platform.")}
      </p>

      {isPro && (
        <div className="rounded-2xl px-3 py-2 bg-emerald-500/10 border border-emerald-400/40 text-[11px] uppercase tracking-widest text-emerald-200 flex items-center justify-between gap-2"
             data-testid="upgrade-already-pro">
          <div className="flex items-center gap-2">
            <Crown className="w-3.5 h-3.5" />
            {status.tier === 'admin' ? t('upgrade.alreadyTeam', "You're already a WildQuest Team.") : t('upgrade.alreadyPro', "You're already a WildQuest Explorer.")}
          </div>
          {status?.tier === 'pro' && (
            <button
              data-testid="upgrade-manage-btn"
              onClick={openPortal}
              disabled={portalLoading}
              className="normal-case tracking-normal text-[11px] font-medium underline underline-offset-2 disabled:opacity-60"
            >
              {portalLoading ? t('upgrade.opening', 'Opening…') : t('upgrade.manageSubscription', 'Manage subscription')}
            </button>
          )}
        </div>
      )}

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PlanCard
          plan="monthly"
          testid="plan-monthly"
          label={t('upgrade.planMonthly', 'Monthly')}
          price={`$${monthly.toFixed(2)}`}
          period={t('upgrade.perMonth', 'per month')}
          tag={t('upgrade.flexibleCancel', 'Flexible · cancel anytime')}
          onSelect={() => startCheckout('monthly')}
          loading={checkout === 'monthly'}
          disabled={isPro}
          bestValueLabel={t('upgrade.bestValue', 'Best value')}
          choosePlanLabel={t('upgrade.choosePlan', 'Choose plan')}
          activeLabel={t('upgrade.activePlan', 'Active')}
        />
        <PlanCard
          plan="yearly"
          testid="plan-yearly"
          label={t('upgrade.planYearly', 'Annual')}
          price={`$${yearly.toFixed(2)}`}
          period={t('upgrade.perYear', 'per year')}
          tag={savings > 0 ? t('upgrade.saveApprox', 'Save approximately {percent}%', { percent: savings }) : t('upgrade.bestValue', 'Best value')}
          highlight
          onSelect={() => startCheckout('yearly')}
          loading={checkout === 'yearly'}
          disabled={isPro}
          bestValueLabel={t('upgrade.bestValue', 'Best value')}
          choosePlanLabel={t('upgrade.choosePlan', 'Choose plan')}
          activeLabel={t('upgrade.activePlan', 'Active')}
        />
      </div>

      {/* Features */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl glass border border-white/10 p-4 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-accent">{t('upgrade.included', 'Included')}</p>
        <ul className="grid grid-cols-1 gap-1.5">
          {FEATURE_KEYS.map((k) => (
            <li key={k} className="flex items-center gap-2 text-sm">
              <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <span>{t(`upgrade.features.${k}`)}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <div className="rounded-2xl px-3 py-2.5 bg-amber-500/5 border border-amber-400/20 text-[10px] text-amber-200/80 space-y-1">
        <p className="font-semibold text-amber-200 uppercase tracking-widest">{t('upgrade.progressProtected', 'Progress Protected')}</p>
        <p>{t('upgrade.progressProtectedBody', 'Subscribing does NOT reset your account. Every discovery, battle, craft and stat carries forward. Your Explorer Nickname stays the same.')}</p>
      </div>
    </div>
  );
}


function PlanCard({ plan, testid, label, price, period, tag, highlight, onSelect, loading, disabled, bestValueLabel, choosePlanLabel, activeLabel }) {
  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.02 }} whileTap={{ scale: disabled ? 1 : 0.99 }}
      onClick={onSelect}
      disabled={disabled || loading}
      data-testid={testid}
      className={`text-left rounded-3xl p-4 border transition-all disabled:opacity-60 ${
        highlight
          ? 'border-amber-400/60 bg-gradient-to-br from-amber-500/15 to-black/50 shadow-[0_0_40px_-8px_rgba(251,191,36,0.55)]'
          : 'border-white/10 bg-white/5'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/70 min-w-0 truncate">{label}</span>
        {highlight && (
          <span className="shrink-0 whitespace-nowrap text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-100 border border-amber-400/50 flex items-center gap-0.5">
            <Sparkles className="w-2.5 h-2.5" />{bestValueLabel}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-serif text-3xl">{price}</span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{period}</span>
      </div>
      <p className={`mt-1 text-[10px] uppercase tracking-widest ${highlight ? 'text-amber-200' : 'text-muted-foreground'}`}>{tag}</p>
      <div className={`mt-3 w-full h-9 rounded-full flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest ${
        highlight ? 'bg-accent text-black' : 'bg-white/10 text-white/90'
      }`}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
          <><Crown className="w-3 h-3" /> {disabled ? activeLabel : choosePlanLabel}</>
        )}
      </div>
    </motion.button>
  );
}
